import React from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'
import { TourMarker } from '../components/ToolTour'
import '../styles/app-mobile.css'

// One prop outlier as a mobile card. Used by PropEvMobile for every row and by
// the Prop Outliers tour (on desktop too), so the tour always shows the real
// card. Everything that depends on the user's settings (stake, odds format)
// arrives already formatted; the card only lays it out.

// One of the three MARKET_TYPE_NAMES copies (with PropEV.jsx and
// PropEvMultiBuilder.jsx); keep them in sync.
const MARKET_TYPE_NAMES = {
  player_disposals:                     'Disposals',
  player_goals:                         'Goals',
  player_marks:                         'Marks',
  player_tackles:                       'Tackles',
  player_kicks:                         'Kicks',
  player_handballs:                     'Handballs',
  player_clearances:                    'Clearances',
  player_disposals_milestones:          'Disposals Milestones',
  player_goals_milestones:              'Goals Milestones',
  player_marks_milestones:              'Marks Milestones',
  player_tackles_milestones:            'Tackles Milestones',
  player_kicks_milestones:              'Kicks Milestones',
  player_handballs_milestones:          'Handballs Milestones',
  player_clearances_milestones:         'Clearances Milestones',
  player_tries_milestones:              'Tries Milestones',
  player_points_milestones:             'Points Milestones',
}

const formatMarketType = (mt) =>
  MARKET_TYPE_NAMES[mt] ||
  mt.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bYds\b/, 'Yards').replace(/\bTds\b/, 'TDs')

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })

const parseSelection = (sel) => {
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}

const noop = () => {}

/**
 * markers: tour only, { market, pick, books, odds, diff, stake, track } -> number
 * children: rendered at the bottom of the card (the expanded odds, error tip)
 */
export default function PropEvCard({
  opp, diffClass, oddsText, stakeText, className = '', style, blurPick = false,
  inMulti = false, legIdx = -1, isExpanded = false, tracked = false, showButtons = true,
  onOpenBook = noop, onMulti = noop, onExpand = noop, onTrack = noop, onShare = noop, onHide = noop,
  markers = {}, children,
}) {
  const mark = (part, style) => markers[part] != null && <TourMarker n={markers[part]} style={style} />
  const anchor = (part) => (markers[part] != null ? { position: 'relative' } : null)
  const blurText = blurPick ? { filter: 'blur(5px)', userSelect: 'none' } : {}

  const { name, dir, line } = parseSelection(opp.selection)
  const lineParts = line ? line.match(/^([\d.]+)\s*(.*)$/) : null
  const num  = lineParts ? lineParts[1] : line
  const stat = lineParts ? lineParts[2] : ''

  return (
    <div className={`bcm-card${inMulti ? ' bcm-card-in-multi' : ''}${className ? ` ${className}` : ''}`} style={style}>

      {/* Market type strip (it clips its own overflow, so a marker sits beside the text inside it) */}
      <div className="bcm-card-type-strip">
        {markers.market != null ? (
          <span style={{ position: 'relative' }}>
            {formatMarketType(opp.market_type)}
            {mark('market', { top: '-4px', right: '-28px' })}
          </span>
        ) : formatMarketType(opp.market_type)}
      </div>

      {/* Diff corner ribbon — gap above our estimated fair odds */}
      <div className={`bcm-ev-corner ${diffClass}`}>
        {`+${opp.ev_percentage.toFixed(2)}%`}
        {mark('diff', { top: '-8px', right: '-6px' })}
      </div>

      {/* Card top: avatar + name */}
      <div className="bcm-card-top">
        <div className="bcm-avatar-wrap">
          <img
            src={getPlayerImageUrl(opp.selection, opp.sport)}
            alt=""
            className={`bcm-avatar${opp.sport === 'NRL' ? ' bc-avatar-body' : ''}`}
            onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
            onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
          />
        </div>
        <div className="bcm-card-name-block">
          <div className="bcm-player-name">{name}</div>
          {dir && (
            <div className={`bc-sel-badge ${dir}`} style={{ ...(blurPick ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}), ...anchor('pick') }}>
              <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
              <span className="bc-sel-badge-num">{num}</span>
              {stat && <span className="bc-sel-badge-stat">{stat}</span>}
              {mark('pick', { top: '-2px', right: '-24px' })}
            </div>
          )}
          <div className="bcm-match-name">{opp.match} · {formatDateTime(opp.commence_time)}</div>
        </div>
      </div>

      {/* Card footer: logo + odds | book name | stake */}
      <div className="bcm-card-footer">
        <div className="bcm-card-footer-left">
          <div className="bcm-footer-books" style={anchor('books')}>
            {opp.bookmakers.slice(0, 4).map((b, i) => (
              BOOKMAKER_LOGOS[b.name] ? (
                <img
                  key={b.name}
                  src={BOOKMAKER_LOGOS[b.name]}
                  alt={b.name}
                  className="bcm-footer-logo"
                  style={{ zIndex: opp.bookmakers.length - i }}
                  onClick={() => onOpenBook(b)}
                />
              ) : null
            ))}
            {opp.bookmakers.length > 4 && (
              <span className="bcm-footer-books-more">+{opp.bookmakers.length - 4}</span>
            )}
            {mark('books')}
          </div>
          <span
            className="bcm-footer-odds"
            style={{ ...blurText, ...anchor('odds') }}
          >
            {oddsText}
            {mark('odds', { top: '-4px', right: '-26px' })}
          </span>
        </div>

        <div className="bcm-card-footer-right">
          <div className="bcm-footer-stake-block" style={anchor('stake')}>
            <span className="bcm-footer-stake-label">Stake</span>
            <span className="bcm-footer-stake-val">{stakeText}</span>
            {mark('stake')}
          </div>
        </div>
      </div>

      {/* Button strip */}
      {showButtons && (
        <div className="bcm-btn-strip">
          <button
            className={`bcm-strip-multi${inMulti ? ' in-multi' : ''}`}
            onClick={onMulti}
          >
            {inMulti && <span>{legIdx + 1}</span>}
          </button>
          <div className="bcm-strip-right">
            <button
              className={`bcm-strip-btn${isExpanded ? ' active' : ''}`}
              onClick={onExpand}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
              </svg>
            </button>
            <button
              className={`bcm-strip-btn${tracked ? ' tracked' : ''}`}
              onClick={onTrack}
              style={anchor('track')}
            >
              {tracked
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              }
              {mark('track')}
            </button>
            <button className="bcm-strip-btn" onClick={onShare}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/>
                <circle cx="6" cy="12" r="3"/>
                <circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
            <button className="bcm-strip-btn hide-btn" onClick={onHide}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
